// src/projects/EmployeeReceipts.js
// The employee's own receipts on ONE project — the whole finances world
// reduced to "what did I buy for this job". They see ONLY receipts they
// uploaded (the server enforces it), and add new ones with a simple form:
// date, store, receipt #, category, notes (120 chars max), amount.
// No +Add Expense, no totals, no other people's spending.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, Receipt } from 'lucide-react';
import { API_BASE } from '../apiBase';

const CATEGORIES = ['Materials', 'Tools', 'Equipment', 'Equipment Rental', 'Fuel', 'Permits', 'Travel', 'Other'];
const NOTES_MAX = 120;

function EmployeeReceipts({ projectId }) {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const authH = () => ({ headers: { Authorization: localStorage.getItem('token') } });

  const emptyForm = () => ({
    date: new Date().toISOString().slice(0, 10),
    store: '', receiptNumber: '', category: 'Materials', notes: '', amount: '',
  });
  const [f, setF] = useState(emptyForm());
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const load = () => {
    axios.get(`${API_BASE}/expenses?project=${projectId}`, authH())
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  };
  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const amt = Number(String(f.amount).replace(/[^\d.]/g, ''));
    if (!isFinite(amt) || amt <= 0) { setErr('Enter a valid amount.'); return; }
    setSaving(true);
    setErr('');
    try {
      await axios.post(`${API_BASE}/expenses`, {
        project: projectId,
        date: new Date(`${f.date}T12:00:00`),
        amount: amt,
        totalAmount: amt,
        store: f.store.trim(),
        receiptNumber: f.receiptNumber.trim(),
        category: f.category,
        notes: f.notes.trim().slice(0, NOTES_MAX),
      }, authH());
      setShowAdd(false);
      setF(emptyForm());
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not save the receipt.');
    } finally { setSaving(false); }
  };

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const input = "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";
  const lbl = "block text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2"><Receipt className="w-5 h-5 text-indigo-400" /> My Receipts</h2>
        <button
          onClick={() => { setShowAdd(v => !v); setErr(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
            showAdd ? 'bg-gray-700 hover:bg-gray-600 text-zinc-200' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
          }`}
        >
          {showAdd ? <><X className="w-3.5 h-3.5" /> Cancel</> : <><Plus className="w-3.5 h-3.5" /> Add Receipt</>}
        </button>
      </div>

      {showAdd && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-5 max-md:p-4 mb-5 shadow-xl">
          <div className="grid grid-cols-3 max-md:grid-cols-1 gap-3">
            <div><label className={lbl}>Date</label><input type="date" className={input} value={f.date} onChange={e => set('date', e.target.value)} /></div>
            <div><label className={lbl}>Store</label><input className={input} placeholder="e.g. Home Depot" value={f.store} onChange={e => set('store', e.target.value)} /></div>
            <div><label className={lbl}>Receipt #</label><input className={input} placeholder="Optional" value={f.receiptNumber} onChange={e => set('receiptNumber', e.target.value)} /></div>
            <div>
              <label className={lbl}>Category</label>
              <select className={input} value={f.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Amount</label><input inputMode="decimal" className={input} placeholder="$0.00" value={f.amount} onChange={e => set('amount', e.target.value)} /></div>
            <div>
              <label className={lbl}>Notes <span className="text-zinc-600 normal-case font-medium">({f.notes.length}/{NOTES_MAX})</span></label>
              <input className={input} maxLength={NOTES_MAX} placeholder="What was it for?" value={f.notes} onChange={e => set('notes', e.target.value.slice(0, NOTES_MAX))} />
            </div>
          </div>
          {err && <p className="text-red-400 text-sm mt-3">{err}</p>}
          <div className="flex justify-end mt-4">
            <button onClick={save} disabled={saving}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {saving ? 'Saving…' : 'Save Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-xl max-md:hidden">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-gray-950/30">
            <tr>
              {['Date', 'Store', 'Receipt #', 'Category', 'Notes', 'Amount'].map(h => (
                <th key={h} className={`px-5 py-3.5 text-xs font-semibold text-zinc-400 uppercase ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {rows === null && <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-600">Loading…</td></tr>}
            {rows !== null && rows.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-600">No receipts yet — tap Add Receipt after your next supply run.</td></tr>
            )}
            {(rows || []).map(r => (
              <tr key={r._id} className="hover:bg-gray-800/40">
                <td className="px-5 py-3 whitespace-nowrap text-sm text-zinc-300">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-sm text-zinc-200 font-medium">{r.store || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-xs text-zinc-400 font-mono">{r.receiptNumber || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-xs"><span className="px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-semibold">{r.category}</span></td>
                <td className="px-5 py-3 text-xs text-zinc-400 max-w-[240px] truncate" title={r.notes}>{r.notes || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-sm text-right font-bold text-emerald-300">{money(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows === null && <p className="text-zinc-600 text-sm text-center py-8">Loading…</p>}
        {rows !== null && rows.length === 0 && (
          <p className="text-zinc-600 text-sm text-center py-8">No receipts yet — tap Add Receipt after your next supply run.</p>
        )}
        {(rows || []).map(r => (
          <div key={r._id} className="bg-gray-900 border border-gray-800/60 rounded-xl p-3.5">
            <div className="flex justify-between items-start gap-2">
              <span className="font-semibold text-sm text-zinc-100">{r.store || '—'}</span>
              <span className="text-emerald-300 font-bold text-sm shrink-0">{money(r.amount)}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-semibold">{r.category}</span>
              <span className="text-[11px] text-zinc-500">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</span>
              {r.receiptNumber && <span className="text-[10px] text-zinc-600 font-mono">#{r.receiptNumber}</span>}
            </div>
            {r.notes && <p className="text-xs text-zinc-400 mt-1.5 break-words">{r.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmployeeReceipts;
